import React, { useState } from "react";
import { Button, Link, Popover, styled, Tab, Tabs } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ClinicLoginWidget } from "./clinicLoginWidget";
import { ClinicColors } from "fe-web-components/styles/clinicTheme";
import { ClinicWithFavorite } from "./clinicManage";
import { ReactComponent as QMarkIcon } from "../../assets/clinic/qmark.svg";
import { STANDALONE_TUTORIAL } from "./clinicSettings/clinicUrls";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useDiagnosticNavigation } from "../diagnosticAgent/useDiagnosticNavigation";
import { useClinicState } from "../../clinic/clinicStateContext";
import { useClinicUserContext } from "./clinicUserContext";

//region [[ Styles ]]

const ClinicTopBarView = styled("div")({
  height: "100%",
  width: "100%",
  display: "flex",
  gap: 10,
  alignItems: "center",
  justifyContent: "start",
});

const LogoutWidget = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginLeft: "auto",
}));

const ClinicTabs = styled(Tabs)({
  height: "100%",
  "& .MuiTabs-flexContainer": {
    height: "100%",
    gap: 6,
  },
  "& .MuiTabs-indicator": {
    backgroundColor: ClinicColors.primary,
    borderTopRightRadius: 6,
    borderTopLeftRadius: 6,
    transition: "height 0.2s ease-in-out",
    height: 5,
  },
});

const ClinicTab = styled(Tab)({
  textTransform: "none",
  position: "relative",
  fontWeight: 700,
  fontSize: 12,
  "&::after": {
    content: '""',
    position: "absolute",
    height: 5,
    width: "100%",
    bottom: 0,
    left: 0,
    background: ClinicColors.secondary,
    borderTopRightRadius: 6,
    borderTopLeftRadius: 6,
    transition: "height 0.2s ease-in-out",
  },
  "&:hover::after": {
    height: 8,
  },
  "&.Mui-selected::after": {
    backgroundColor: ClinicColors.primary,
  },
});

const QMarkButton = styled(Button)({
  minWidth: 0,
  padding: 8,
  cursor: "pointer",
  "& span": {
    display: "flex",
    width: 21,
    height: 21,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 21,
    background: "#FFFFFF",
    boxShadow: "0px 0px 5.00369px rgba(0, 0, 0, 0.23)",
  },
  "& svg": {
    width: 6,
    height: 10,
  },
  "&:hover": {
    background: "transparent",
    "& span": {
      boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.23)",
    },
  },
});

const useStyles = makeStyles({
  popover: {
    fontSize: 14,
    "& .MuiPopover-paper": {
      marginTop: 20,
      padding: "14px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    "& a": {
      color: ClinicColors.textMain,
      textDecoration: "none",
      cursor: "pointer",
    },
    "& a:hover": {
      color: ClinicColors.primary,
      textDecoration: "none",
    },
    "& .logout-link": {
      color: ClinicColors.textMain,
      padding: 0,
      fontSize: 14,
      background: "none",
    },
    "& .logout-link:hover": {
      color: ClinicColors.primary,
    },
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface ClinicTopBarProps {
  onShowClinicSelection: () => void;
  currentClinic: ClinicWithFavorite;
  gotoClinic: (clinicId: string) => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ClinicTopBar = ({
  currentClinic,
  ...props
}: ClinicTopBarProps) => {
  const { userClinics, loading } = useClinicUserContext();
  const [
    {
      clinicData: { displayName },
    },
  ] = useClinicState();
  const favClinics = userClinics.filter((c) => c.isFavorite);
  const [
    supportAnchorEl,
    setSupportAnchorEl,
  ] = useState<HTMLButtonElement | null>(null);
  const { setFeedbackDialogProps } = useDiagnosticNavigation();
  const classes = useStyles();

  const handleHelpClicked = (e: any) => {
    setSupportAnchorEl(e);
    evt({
      category: CATEGORY.CLINIC_MANAGEMENT,
      action: ACTION.CLINIC_HELP_CLICKED,
      props: {
        clinicName: currentClinic.name,
      },
    });
  };

  const closeHelpMenu = () => {
    setSupportAnchorEl(null);
  };

  return (
    <ClinicTopBarView className="clinic-top-bar">
      {!loading && favClinics.length && (
        <ClinicTabs
          value={currentClinic.id}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="scrollable clinic tabs"
        >
          {//if current clinic is not in favorites - add it to the beginning of the tabs list
          !currentClinic.isFavorite && (
            <ClinicTab
              onClick={() => props.gotoClinic(currentClinic.id)}
              value={currentClinic.id}
              label={currentClinic.displayName || currentClinic.name}
              key={"tab" + currentClinic.id}
            />
          )}
          {favClinics.map((clinic) => (
            <ClinicTab
              onClick={() => props.gotoClinic(clinic.id)}
              value={clinic.id}
              label={clinic.displayName || clinic.name}
              key={"tab" + clinic.id}
            />
          ))}
        </ClinicTabs>
      )}
      {!loading && userClinics.length && (
        <>
          {userClinics.length - favClinics.length > 0 && (
            <Button
              onClick={props.onShowClinicSelection}
              color={"primary"}
              size={"small"}
              style={{ lineHeight: 1.2, whiteSpace: "nowrap" }}
            >
              {userClinics.length - favClinics.length}{" "}
              {favClinics.length ? "more..." : "Clinics"}
            </Button>
          )}
        </>
      )}
      {!loading && !userClinics.length && <span>No available clinics</span>}
      {loading && <span>Loading your clinics...</span>}
      <LogoutWidget>
        <QMarkButton
          onClick={(e) => handleHelpClicked(e.currentTarget)}
          title="Support"
          disableRipple
        >
          <QMarkIcon />
        </QMarkButton>
        <ClinicLoginWidget className={classes.popover} />
      </LogoutWidget>
      <Popover
        className={classes.popover}
        id="menu-appbar"
        anchorEl={supportAnchorEl}
        keepMounted
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={!!supportAnchorEl}
        onClose={closeHelpMenu}
      >
        <Link
          target="_blank"
          href={STANDALONE_TUTORIAL}
          onClick={closeHelpMenu}
        >
          Getting Started
        </Link>
        <Link
          onClick={() => {
            closeHelpMenu();
            setFeedbackDialogProps({
              open: true,
              caseId: null,
              clinicName: displayName,
            });
          }}
        >
          Send Feedback
        </Link>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
          }}
        >
          Contact us:
          <Link
            href={"mailto:support@kahun.com"}
            style={{ textDecoration: "underline" }}
          >
            support@kahun.com
          </Link>
        </div>
      </Popover>
    </ClinicTopBarView>
  );
};
